import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useUserStore } from '@/stores/user'
import { useDeviceStore } from '@/stores/device'
import { storeToRefs } from 'pinia'
import {
  Role,
  RoleId,
  type BaseDevice,
  type Config,
  type ConfigCapability,
  type ItineraryCapability,
} from '@rapoo/device-sdk'
import { useConfigurationStore } from '@/stores'

/**
 * 网吧渠道下的管理员权限判断
 *
 * - cafeBaseAdminCanShow：网吧管理员 + VT 底座，专属可见（页面 / 播放器）
 * - canAddKeyboardLighting：网吧管理员不可新增自定义灯光
 * - canShowKeyboardItinerary：是否显示行程（网吧管理员不可；设备无行程能力也不可）
 * - canShowKeyboardKeys：是否显示改键（网吧管理员不可）
 *
 * @param device - 可选。指定要判断的设备，不传则使用 currentDevice
 *
 * @example
 * ```ts
 * const { cafeBaseAdminCanShow, canAddKeyboardLighting, canShowKeyboardItinerary, canShowKeyboardKeys } =
 *   useAdminOnly()
 * ```
 */
export function useAdminOnly(device?: MaybeRefOrGetter<BaseDevice | null | undefined>) {
  const userStore = useUserStore()
  const deviceStore = useDeviceStore()
  const { isAdmin } = storeToRefs(userStore)
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { configs } = storeToRefs(configStore)

  const channel = import.meta.env.VITE_CHANNEL as string

  const targetDevice = computed(() => {
    // 传了设备参数则使用，否则回退到 currentDevice
    if (device !== undefined) return toValue(device)
    return currentDevice.value
  })

  const isCafe = computed(() => channel === 'china_cybercafe')

  // 网吧渠道下的管理员（不绑定具体设备形态）
  const isCafeAdmin = computed(() => isCafe.value && isAdmin.value)

  // 网吧管理员专属可见底座内容
  const cafeBaseAdminCanShow = computed(() => {
    const isVtDevice = targetDevice.value?.manifest?.driver === 'VT_nrf54L_Base'
    return isCafeAdmin.value && isVtDevice
  })

  const canShowBaseScreenContent = computed(() => !isCafe.value || cafeBaseAdminCanShow.value)

  const itineraryCap = computed(() => {
    return targetDevice.value?.manifest.capabilities.find(
      (item): item is ItineraryCapability => item.type === 'itinerary',
    )
  })

  /** 网吧管理员不可新增自定义键盘灯光（设备能力另在灯光列表侧判断）。 */
  const canAddKeyboardLighting = computed(() => !isCafeAdmin.value)

  /**
   * 是否显示行程页 / Tab。
   * 网吧管理员隐藏；设备无 itinerary 或 hasTravel === false 时也隐藏。
   */
  const canShowKeyboardItinerary = computed(() => {
    if (isCafeAdmin.value) return false
    if (!itineraryCap.value) return false
    return itineraryCap.value.config.hasTravel !== false
  })

  /** 是否显示改键页 / Tab。网吧管理员隐藏。 */
  const canShowKeyboardKeys = computed(() => !isCafeAdmin.value)

  // 设备配置能力
  const configCap = computed(() => {
    return targetDevice.value?.manifest.capabilities.find((item) => item.type === 'config') as
      | ConfigCapability
      | undefined
  })

  const isAdminRole = (roles: Role[]) => {
    return roles.includes(RoleId.superAdmin) || roles.includes(RoleId.admin)
  }

  // 根据角色过滤可见配置
  const configRoleVisible = computed(() => {
    if (!configCap.value?.config.configSlots) return configs.value
    const configSlots = configCap.value.config.configSlots
    return configSlots
      .filter((slot) =>
        isAdmin.value ? isAdminRole(slot.roles) : slot.roles.includes(RoleId.user),
      )
      .map((slot) => configs.value.find((item) => item.index === slot.id))
      .filter((item): item is Config => item !== undefined)
  })

  // 当前角色可用配置数（有配置槽位时按角色过滤，无则使用 maxConfig）
  const configRoleMaxCount = computed(() => {
    const slots = configCap.value?.config.configSlots
    if (!slots) return configCap.value?.config.maxConfig ?? Infinity
    return slots.filter((slot) =>
      isAdmin.value ? isAdminRole(slot.roles) : slot.roles.includes(RoleId.user),
    ).length
  })

  return {
    cafeBaseAdminCanShow,
    canShowBaseScreenContent,
    canAddKeyboardLighting,
    canShowKeyboardItinerary,
    canShowKeyboardKeys,
    configRoleVisible,
    configRoleMaxCount,
  }
}
